import { useState } from "react";
import AtriumKpiTile from "@/components/atrium/AtriumKpiTile";
import { useDashboardSnapshot } from "@/hooks/useDashboardSnapshot";
import { useInboxState } from "@/hooks/useInboxState";
import { useJobRuns } from "@/hooks/useJobRuns";
import { agoLabel, formatTime } from "@/lib/format";

type OpsTab = "snapshot" | "runs" | "schedules";

const TABS: [OpsTab, string][] = [
  ["snapshot", "Snapshot"],
  ["runs", "Recent runs"],
  ["schedules", "Schedules"],
];

function statusToDot(status: string) {
  if (status === "success") return "var(--a-ok)";
  if (status === "partial") return "var(--a-warn)";
  return "var(--a-bad)";
}

export default function AtriumOps() {
  const [tab, setTab] = useState<OpsTab>("snapshot");
  const snapshot = useDashboardSnapshot();
  const inbox = useInboxState();
  const runs = useJobRuns({ sinceHours: 48, limit: 30 });
  const counts = snapshot.data?.counts ?? {};
  const errors = snapshot.data?.writer_errors ?? {};
  const writerStatus = snapshot.data?.writer_status ?? "missing";

  return (
    <div className="p-5 flex flex-col gap-4">
      <div>
        <div className="text-[24px] font-semibold tracking-[-0.02em]">Ops</div>
        <div className="text-[13px] mt-0.5" style={{ color: "var(--a-muted)" }}>
          Snapshot writer, job runs, and vault inbox state from Agency Supabase.
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <AtriumKpiTile
          label="Writer"
          value={writerStatus}
          sub={snapshot.data?.snapshot_at ? agoLabel(snapshot.data.snapshot_at) : "no snapshot row"}
          color={writerStatus === "success" ? "var(--a-ok)" : writerStatus === "partial" ? "var(--a-warn)" : "var(--a-bad)"}
        />
        <AtriumKpiTile label="Emails pending" value={counts.emails_pending ?? "-"} />
        <AtriumKpiTile label="Alerts active" value={counts.alerts_active ?? "-"} color={counts.alerts_active ? "var(--a-bad)" : undefined} />
        <AtriumKpiTile label="Vault inbox" value={inbox.data?.inbox_count ?? "-"} sub={`${inbox.data?.failed_count ?? 0} failed`} color={inbox.data?.failed_count ? "var(--a-bad)" : undefined} />
      </div>

      <div className="a-card overflow-hidden">
        <div className="flex" style={{ borderBottom: "1px solid var(--a-border)" }}>
          {TABS.map(([id, label]) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className="px-4 py-2.5 text-[12px] font-medium"
              style={{
                background: "transparent",
                color: tab === id ? "var(--a-text)" : "var(--a-muted)",
                borderBottom: tab === id ? "2px solid var(--a-accent)" : "2px solid transparent",
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "snapshot" && (
          <div className="p-4 text-[13px]">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="rounded-[7px] border p-3" style={{ borderColor: "var(--a-border)", background: "var(--a-bg)" }}>
                <div className="text-[10px] uppercase tracking-[0.06em]" style={{ color: "var(--a-muted)" }}>Last snapshot</div>
                <div className="mono mt-1">{snapshot.data?.snapshot_at ? `${formatTime(snapshot.data.snapshot_at)} - ${agoLabel(snapshot.data.snapshot_at)}` : "missing"}</div>
              </div>
              <div className="rounded-[7px] border p-3" style={{ borderColor: "var(--a-border)", background: "var(--a-bg)" }}>
                <div className="text-[10px] uppercase tracking-[0.06em]" style={{ color: "var(--a-muted)" }}>Writer errors</div>
                <div className="mono mt-1">{Object.keys(errors).length ? Object.keys(errors).join(", ") : "none"}</div>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-2">
              {Object.entries(counts).map(([key, value]) => (
                <div key={key} className="rounded-[7px] border p-2" style={{ borderColor: "var(--a-border)" }}>
                  <div className="text-[10px] uppercase tracking-[0.06em]" style={{ color: "var(--a-faint)" }}>{key.replace(/_/g, " ")}</div>
                  <div className="mono text-[16px] mt-1">{value}</div>
                </div>
              ))}
              {Object.keys(counts).length === 0 && (
                <div className="text-[12px]" style={{ color: "var(--a-muted)" }}>No snapshot counts available.</div>
              )}
            </div>
          </div>
        )}

        {tab === "runs" && (
          <div>
            {(runs.data ?? []).length === 0 && !runs.isLoading && (
              <div className="px-[14px] py-3 text-[12px]" style={{ color: "var(--a-muted)" }}>
                No recent runs.
              </div>
            )}
            {(runs.data ?? []).map((r, i) => (
              <div key={r.id} className="flex items-center gap-2.5 px-[14px] py-2.5 text-[13px]" style={{ borderTop: i ? "1px solid var(--a-border)" : "none" }}>
                <span className="a-dot" style={{ background: statusToDot(r.status) }} />
                <span className="mono w-[160px] overflow-hidden text-ellipsis whitespace-nowrap">{r.job}</span>
                <span className="flex-1 text-[12px] overflow-hidden text-ellipsis whitespace-nowrap" style={{ color: "var(--a-muted)" }}>
                  {r.error ?? r.client ?? r.artifact_path ?? "-"}
                </span>
                <span className="mono text-[11px]" style={{ color: "var(--a-faint)" }}>
                  {r.started_at ? formatTime(r.started_at) : "-"} - {agoLabel(r.started_at)}
                </span>
              </div>
            ))}
            {runs.isLoading && (
              <div className="px-[14px] py-3 text-[12px]" style={{ color: "var(--a-muted)" }}>Loading...</div>
            )}
          </div>
        )}

        {tab === "schedules" && (
          <div className="px-[14px] py-6 max-w-[620px] text-[13px] leading-relaxed" style={{ color: "var(--a-muted)" }}>
            Schedule display is disabled until the frontend receives generated schedule data from
            `bot/config.yaml` or a Supabase cache. The cockpit is not showing hardcoded next-run
            times.
          </div>
        )}
      </div>
    </div>
  );
}
